import * as React from "react";
import {cx} from "emotion";

import "./setup-wizard.scss";

import {AutoSave}       from "@Common/react/async/auto-save";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {fragments}      from "@Common/react/fragments";
import {Invoke}         from "@Common/react/invoke";
import {keyed}          from "@Common/react/keyed";
import {OnMounted}      from "@Common/react/on-mounted";
import {OnUnmounted}    from "@Common/react/on-unmounted";
import {Static2}        from "@Common/react/static-2";
import {UseState}       from "@Common/react/use-state";
import {ConfirmDialog2} from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog-2/confirm-dialog-2";
import {scrollIntoView} from "@Common/ui-components/light/scroll-into-view";

import {groupEnrollmentEditors} from "../common/group-enrollment-editors";

import {BigSvgLineSteps} from "./big-svg-line-steps/big-svg-line-steps";

export const SetupWizard = (
    {
        steps, enrollment,
        saveEnrollment,
        initTab,
        setNavBlock,
        onShowStep,
        onClose,
    }
) => cs(

    consumeContext("apis"),

    ["steps", (_, next) => next(steps)],
    ["stepIndex", ({}, next) => UseState({
        initValue: initTab ?? 0,
        next,
    })],
    ({stepIndex}, next) => fragments(
        next(),
        onShowStep && cs(
            keyed(stepIndex.value),
            () => Invoke({
                action: () => {
                    onShowStep(stepIndex.value);
                },
            })
        ),
    ),
    ["activeStep", ({stepIndex, steps}, next) => next(steps[stepIndex.value])],
    // ({stepIndex}, next) => keyed1({key: stepIndex.value, next}),
    // ["activeStepObj", ({activeStep}, next) => activeStep.service({next})],

    ["editor", ({steps}, next) => next(groupEnrollmentEditors(steps.map((s) => s.enrollmentEditor).filter(v=>v)))],

    ["apiSave", ({editor}, next) => next(async ({newEnrollment=false}={}) => {

        const {newValue, onSaved} = editor.prepareSave(enrollment);

        const result = await saveEnrollment(newValue, {newEnrollment});

        if (!result) {
            await onSaved();
        }
    })],
    ["autoSave", ({editor, apiSave}, next) => AutoSave({
        save: apiSave,
        // active: (editor.valid && editor.changed),
        active: !editor.loading && editor.changed && editor.needUrgentSaving,
        next,
    })],

    ["showErrors", (_, next) => UseState({next})],
    ["tabDomRef", (_, next) => Static2({getInitValue: () => ({}), next})],

    ["stepsUI", (_, next) => BigSvgLineSteps({next})],
    ["confirmDialog", ({}, next) => ConfirmDialog2({next})],
    ({editor, confirmDialog}, next) => fragments(
        next(),
        !!setNavBlock(!editor.changed ? null : (
            async () => !await confirmDialog.show({
                actionName: "exit",
                message: "Do you want to save your changes?",
                actionContent: "",
                buttons: {
                    confirmLabel: "Don't Save",
                },
            })
        )),
        OnUnmounted({
            action: () => setNavBlock(null),
        }),
    ),
    ({
        activeStep, stepIndex, steps, tabDomRef, showErrors, editor,
        apiSave, stepsUI, confirmDialog,
    }) => (
        <div className="setup-wizard-5sw">
            <div className="nav" ref={tabDomRef.set}>
                {stepsUI.render({
                    steps: steps.map((step, i) => cs(
                        ["disabled", (_, next) => next(
                            !enrollment?.enrolled &&
                            stepIndex.value < i &&
                            steps.slice(0, i).some((s) => !s.enrollmentEditor?.valid)
                        )],
                        ["active", (_, next) => next(activeStep === step)],
                        ({active, disabled}) => ({
                            disabled,
                            active,
                            icon: step.icon,
                            hasError: (
                                stepIndex.value > i &&
                                !active &&
                                !step.enrollmentEditor?.valid &&
                                !steps.some((step) => step.enrollmentEditor?.loading)
                            ),
                            completed: stepIndex.value > i && (steps[i].enrollmentEditor == null || steps[i].enrollmentEditor.valid),
                            label: step.label,
                            onClick: () => !disabled && stepIndex.onChange(i),
                        })
                    )),
                })}

                {OnMounted({
                    action: () => {
                        scrollIntoView(tabDomRef.get());
                    }
                })}
            </div>

            <div className="content">
                {(editor.loading) && (
                    <div className="loading">
                        <i className="fa fa-spinner fa-pulse"/>
                        {editor.loading ? (
                            "Loading..."
                        ) : (
                            "Saving..."
                        )}

                    </div>
                )}

                {activeStep.render({
                    showErrors: showErrors.value,
                })}
            </div>

            <div className="footer">
                <div className="left">
                    {!enrollment?.enrolled && stepIndex.value > 0 ? cs(
                        () => (
                            <button {...{
                                className: "cancel",
                                onClick: async () => {
                                    stepIndex.change(v => v - 1);
                                    scrollIntoView(tabDomRef.get());
                                },

                                "xd-qa-type":"button",
                                "xd-qa-name":"Back",
                            }}>
                                Back to {steps[stepIndex.value - 1].label}
                            </button>
                        )
                    ) : cs(
                        ({}) => (
                            <button {...{
                                className: "cancel",
                                onClick: async () => {
                                    // console.log("editor.changed", editor.changed);
                                    if (!editor.changed || await confirmDialog.show({
                                        actionName: "exit",
                                        message: "Do you want to save your changes?",
                                        actionContent: "",
                                        buttons: {
                                            confirmLabel: "Don't Save",
                                        },
                                    })) {
                                        onClose();
                                    }
                                },
                                "xd-qa-type":"button",
                                "xd-qa-name":"Cancel",
                            }}>
                                Cancel
                            </button>
                        )
                    )}
                </div>

                <div className="right">

                    {cs(
                        ({}, next) => (!enrollment?.enrolled && stepIndex.value < steps.length - 1) && next(),
                        ["saving", (_, next) => UseState({next})],
                        ({saving}) => (
                            <button {...{
                                className: "extra",
                                disabled: saving.value || editor.loading,
                                onClick: async () => {
                                    if (editor.changed || enrollment?._id == null) {
                                        saving.onChange(true);
                                        await apiSave();
                                        saving.onChange(false);
                                    }
                                    onClose();
                                },

                                "xd-qa-type": "button",
                                "xd-qa-name": "Save for Later",
                            }}>
                                {saving.value ? (
                                    "Saving..."
                                ) : (
                                    "Save for Later"
                                )}
                            </button>
                        )
                    )}

                    {!enrollment?.enrolled ? (
                        stepIndex.value < steps.length - 1 ? <>
                            <button {...{
                                className: cx("next", {disabled: steps.slice(0, stepIndex.value+1).some((s) => !s.enrollmentEditor?.valid)}),
                                onClick: () => {
                                    if (!checkError({activeStep, showErrors, steps, stepIndex, stepsUI, tabDomRef})) {
                                        return;
                                    }

                                    stepIndex.change(v => v + 1);
                                    scrollIntoView(tabDomRef.get());
                                },

                                "xd-qa-type":"button",
                                "xd-qa-name":"Continue",
                            }}>
                                Continue to {steps[stepIndex.value + 1].label}
                            </button>
                        </> : cs(
                            ["saving", (_, next) => UseState({next})],
                            ({saving}) => (
                                <button {...{
                                    disabled: saving.value,
                                    className: cx("complete", {disabled: !editor?.valid}),
                                    onClick: async () => {
                                        if (!checkError({activeStep, showErrors, steps, stepIndex, stepsUI, tabDomRef})) {
                                            return;
                                        }

                                        saving.onChange(true);
                                        try {
                                            await apiSave({newEnrollment: true});

                                            onClose();
                                        } finally {
                                            saving.onChange(false);
                                        }
                                    },

                                    "xd-qa-type":"button",
                                    "xd-qa-name":"Complete Enrollment",
                                }}>
                                    {saving.value ? (
                                        "Saving..."
                                    ) : (
                                        "Complete Enrollment"
                                    )}
                                </button>
                            ),
                        )
                    ) : cs(
                        ["saving", (_, next) => UseState({next})],
                        ({saving}) => (
                            <button {...{
                                disabled: saving.value,
                                className: cx("complete", {
                                    disabled: (
                                        activeStep.enrollmentEditor?.loading ||
                                        !activeStep.enrollmentEditor?.valid
                                    ),
                                }),
                                onClick: async () => {
                                    if (!activeStep.enrollmentEditor?.valid) {
                                        activeStep.enrollmentEditor?.errorsHandling?.highlightErrors?.();
                                        showErrors.onChange(true);
                                        return;
                                    }

                                    // console.log(activeStep)
                                    saving.onChange(true);
                                    try {
                                        await apiSave();
                                        onClose();
                                    } finally {
                                        saving.onChange(false);
                                    }
                                },

                                "xd-qa-type":"button",
                                "xd-qa-name":"Save & Return to Dashboard",
                            }}>
                                {saving.value ? (
                                    "Saving..."
                                ) : (
                                    "Save & Return to Dashboard"
                                )}
                            </button>
                        ),
                    )}
                </div>
            </div>

        </div>
    ),
);

const checkError = ({activeStep, showErrors, steps, stepIndex, stepsUI, tabDomRef}) => {
    const errorStepIndex = steps.slice(0, stepIndex.value).findIndex((s) => !s.enrollmentEditor?.valid);
    if (errorStepIndex > -1) {
        stepsUI.glowStep(errorStepIndex);
        scrollIntoView(tabDomRef.get());
        return false;
    }

    if (!activeStep.enrollmentEditor?.valid) {
        activeStep.enrollmentEditor?.errorsHandling?.highlightErrors?.();
        showErrors.onChange(true);
        return false;
    }

    return true;
};

